import React, { useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SummerInReview = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await apiClient.get('/api/summer-review');
        setStats(response.data);
      } catch (err) {
        setError('Failed to load Summer In Review.');
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const disableAllNotifications = async () => {
    setDisabling(true);
    try {
      // Attempt to unsubscribe from push notifications across all registrations
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            if (!reg.pushManager) continue;
            try {
              const sub = await reg.pushManager.getSubscription();
              if (sub) {
                try { await apiClient.post('/api/push/unsubscribe', { endpoint: sub.endpoint }); } catch (_) {}
                try { await sub.unsubscribe(); } catch (_) {}
              }
            } catch (_) {}
          }
        } catch (_) {}
      }

      // Disable all server-side push subscriptions for the user as a safety net
      try { await apiClient.post('/api/push/disable-all'); } catch (_) {}

      // Disable server-side scheduling alerts (used for daily notifications)
      // Persist notification preference in DB
      await apiClient.put('/api/profile/notifications', { scheduling_alerts: false });

      setDisabled(true);
    } catch (e) {
      // Surface a lightweight error message but keep UI responsive
      setError('Failed to disable notifications. Please try again.');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Preparing your Summer In Review...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="summer-review-container">
      <div className="summer-review-card">
        <h1 className="summer-review-title">Your Summer In Review</h1>
        <p className="summer-review-subtitle">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username}</p>

        <div className="summer-review-grid">
          <div className="summer-review-item">
            <div className="sr-label">Days Scheduled</div>
            <div className="sr-value">{stats?.daysScheduled ?? 0}</div>
          </div>
          <div className="summer-review-item">
            <div className="sr-label">Most Common Beach</div>
            <div className="sr-value">{stats?.mostCommonBeach || '—'}</div>
          </div>
          <div className="summer-review-item">
            <div className="sr-label">Most Common Stand Partner</div>
            <div className="sr-value">{stats?.mostCommonPartner || '—'}</div>
          </div>
          <div className="summer-review-item">
            <div className="sr-label">Most Common Vehicle</div>
            <div className="sr-value">{stats?.mostCommonVehicle || '—'}</div>
          </div>
          <div className="summer-review-item">
            <div className="sr-label">Longest Working Streak</div>
            <div className="sr-value">{stats?.longestStreak ?? 0} days</div>
          </div>
        </div>

        <div className="summer-review-actions" style={{ gap: '0.75rem' }}>
          <button 
            className="btn btn-primary"
            onClick={disableAllNotifications}
            disabled={disabling || disabled}
          >
            {disabled ? 'Notifications Disabled' : (disabling ? 'Disabling…' : 'Disable All Notifications')}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleLogout}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummerInReview;


